import { type JanusCtx, type JanusReaders, type Row, type ObjectDamageEntry,
  inventoryId, ecId, ecLean, scalar, record, upper, HandBack, handBack, definitionNotHeld, insertRoll, activeStatuses, itemField, requiredInt } from '../ability/context.js';
import { createVitalityReader } from '../compile/vitality-reader.js';
import { encounterField, posCoord } from '../../shared/array-types.js';
import { resolveObjectDestruction, applyObjectDestruction, type ObjectDestroyCommand } from './destroy-object.js';
import { prepareDestructionSave } from './destruction-save.js';
import { LINK_STATUS_IDS, statusRows, statusItemId, statusRemovalBefore } from '../statuses/expiry.js';
import { retireDeathStatus } from '../death/status-removal.js';
import { wallPieceFingerprint } from './wall-run-damage.js';
import { applyObjectDamageTotal, objectFallUnmodelled } from './object-fall-damage.js';
import { normalizeInventoryGeneration } from '../compile/inventory-generation.js';
import { readInventoryField } from '../compile/inventory-raw-readers.js';
import { resolveObjectVitality, installObjectVitality } from './object-vitality.js';
import { cancelDestroyedInventoryStatuses } from './destruction-statuses.js';

export type InventoryDamageNeed = { kind: 'links' | 'corpse' | 'vitality'; id: string };
/** Fetchable facts discovered by a dry run, never a destruction decision made with unknown facts. */
export class InventoryDamageFactsNeeded extends HandBack {
  constructor(public readonly needs: InventoryDamageNeed[]) {
    super(`inventory-damage:${needs[0]!.kind}-not-held:${needs[0]!.id}`);
  }
}

/** Collision half damage is an already-summed RAW total: no object resistance, mitigation or damage log. */
export function applyRawDamageToInventory(ctx: JanusCtx, caster: Row, source: Row, total: number): void {
  if (!Number.isSafeInteger(total) || total < 0) handBack('inventory-damage:raw:invalid-total');
  if (total === 0 || ctx.inventoryChanged.some(change => change.inventory_id === inventoryId(source) && change.destruction)) return;
  const target = inventoryDamageTarget(ctx.read, source, ctx.encounter);
  const reason = objectFallUnmodelled({ ownLayerKeys: Object.keys(record(target.item_layer_types) ?? {}),
    statuses: activeStatuses(ctx, source).map(status => ({ id: upper(status.status_item_id),
      definition: record(status.status_arr) ?? ctx.read.definition(upper(status.status_item_id)) })),
    itemType: String(itemField(target, 'item_type') ?? ''),
    wallRun: !!normalizeInventoryGeneration(ctx.read.inventoryField
      ? ctx.read.inventoryField(target, 'inventory_generation') : readInventoryField(target, 'inventory_generation'))?.wallRun,
    privateToViewer: false, rollTypeModifiers: false, campaignHeld: true });
  if (reason) handBack('inventory-damage:raw:' + reason);
  const vitality = record(record(target.computed)?.vitality);
  if (!vitality) throw new InventoryDamageFactsNeeded([{ kind: 'vitality', id: inventoryId(source) }]);
  const before = requiredInt(vitality?.v_current, 'inventory-damage:raw:v-current-not-held');
  const tempBefore = requiredInt(vitality?.v_temp ?? 0, 'inventory-damage:raw:v-temp-not-held');
  const maximum = requiredInt(vitality?.effective_v_max ?? vitality?.v_max, 'inventory-damage:raw:v-max-not-held');
  const damage = applyObjectDamageTotal({ total, vCurrent: before, vTemp: tempBefore, effectiveVMax: maximum,
    destroyedByDamageType: [], damageTypeIds: [] });
  Object.assign(vitality!, { v_current: damage.vCurrent, base_v_current: damage.vCurrent, v_temp: damage.vTemp, base_v_temp: damage.vTemp });
  Object.assign(record(target.inventory)!, { v_current: damage.vCurrent, v_temp: damage.vTemp });
  Object.assign(source, target);
  saveAbilityObjectDamage(ctx, caster, source, { inventory_id: inventoryId(source), before: { v_current: before, v_temp: tempBefore },
    v_current: damage.vCurrent, v_temp: damage.vTemp, destroyed: damage.destroyed, forced: false,
    damageTypeIds: [], total, afterRow: ctx.rows.length });
}

/** Bulk corpse stripping does not fire ordinary loss triggers. Connected/summon cleanup needs a complete browser plan. */
function planCorpseCleanup(ctx: JanusCtx, command: ObjectDestroyCommand): void {
  for (const child of command.spill?.destroyed ?? []) planCorpseCleanup(ctx, child);
  if (!command.corpse) return;
  const id = upper(command.corpse.encounter_creature_id), member = ctx.members.get(id);
  if (!member) handBack(`inventory-damage:corpse-not-held:${id}`);
  if (Number(ecLean(member, 'encounter_creature_dead') ?? 0) !== 1) handBack('inventory-damage:corpse-death-state-not-held');
  // Prone/Rotting name this target; a caster projection's holder cannot supply
  // an unknown target. Only known self-casts use the target-side bulk retire.
  if (statusRows(member, 'caster').some(status => statusRemovalBefore(status).status_target_id !== id))
    handBack('inventory-damage:corpse-caster-cleanup');
  const statuses = ctx.read.statuses(member);
  const connections = new Set(statuses.map(status => upper(statusRemovalBefore(status, id).status_connected_id)).filter(Boolean));
  const holders = [ctx.encounter, ...ctx.members.values()];
  if (statuses.some(status => LINK_STATUS_IDS.has(statusItemId(status))) || holders.some(holder => ctx.read.statuses(holder).some(status => {
    const before = statusRemovalBefore(status, ecId(holder));
    return before.status_target_id !== id && connections.has(upper(before.status_connected_id));
  })))
    handBack('inventory-damage:corpse-connected-cleanup');
  for (const status of statuses) {
    const statusId = upper(scalar(status.status_id)), itemId = statusItemId(status);
    if (!/^[0-9A-F-]{36}$/.test(statusId) || !itemId) handBack('inventory-damage:corpse-status-not-held');
    retireDeathStatus(ctx, holders, { ...status, ...statusRemovalBefore(status, id) });
  }
  ctx.statusesAdded = ctx.statusesAdded.filter(status => upper(status.target) !== id);
  Object.assign(record(member.encounter_creatures)!, { encounter_creature_dead: 1, v_current: 0, v_temp: 0 });
  command.corpse.retireAttachments = true;
}

/** Floor facets carry lean instances; their template supplies the omitted vitality defaults. */
export function inventoryDamageTarget(read: JanusReaders, source: Row, encounter: Row | null = null): Row {
  const row = structuredClone(source);
  const templateId = upper(read.inventoryField?.(row, 'inventory_item_id') ?? record(row.inventory)?.inventory_item_id);
  const template = read.definition(templateId);
  if (template) {
    row.items ??= structuredClone(template.items);
    row.item_layer_types = { ...record(template.item_layer_types), ...record(row.item_layer_types) };
    row.pictures ??= structuredClone(template.pictures);
  }
  row.catalog = { ...(template ? { [templateId]: template } : {}), ...record(row.catalog) };
  const vitality = resolveObjectVitality(source, encounter, read.definition);
  if (vitality) installObjectVitality(row, vitality);
  return row;
}

/** apply_damage_to_target's inventory branch; destruction uses the existing rubble/spill planner. */
export function saveAbilityObjectDamage(ctx: JanusCtx, caster: Row, target: Row, hit: ObjectDamageEntry): void {
  if (ctx.inventoryChanged.some(change => change.inventory_id === hit.inventory_id && change.destruction)) return;
  let destruction: ObjectDestroyCommand | undefined;
  if (hit.destroyed) {
    const templateId = upper(ctx.read.inventoryField?.(target, 'inventory_item_id') ?? record(target.inventory)?.inventory_item_id);
    if (!ctx.read.definition(templateId)) definitionNotHeld('inventory-damage:definition-not-held', templateId);
    if (!ctx.read.inventoryDestruction) handBack('inventory-damage:destruction-readers-not-held');
    const encounterId = upper(encounterField(ctx.encounter, 'encounter_id'));
    // A linked creature's death state is this verdict's: the live clones replace their roster rows (a revived creature's body breaks as a plain object).
    const creatures = [...ctx.members.values(), ...(Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr : [])
      .filter(row => !ctx.members.has(ecId(record(row))))];
    const view = { ...ctx.encounter, encounter_creatures_arr: creatures, encounter_inventories_arr: [target,
      ...(Array.isArray(ctx.encounter.encounter_inventories_arr) ? ctx.encounter.encounter_inventories_arr : [])
        .filter(row => inventoryId(record(row)) !== inventoryId(target))] };
    const destructionReaders = ctx.read.inventoryDestruction!(target);
    const createdIds = new Set(ctx.inventoryChanged.flatMap(change => (change.carve?.rows ?? []).map(inventoryId)));
    const linkOrigins = new Map<string, string>();
    const remember = (command: ObjectDestroyCommand): void => {
      for (const child of command.spill?.destroyed ?? []) remember(child);
      for (const row of command.frame.add) createdIds.add(inventoryId(row as unknown as Row));
      // Replacement rubble and waypoints retain their source's remote links.
      for (const placed of [command.rubble, command.waypoint]) if (placed && (command.links || command.waypoint))
        linkOrigins.set(upper(placed.inventory_id), command.inventory_id);
    };
    for (const change of ctx.inventoryChanged) if (change.destruction) remember(change.destruction);
    const needs: InventoryDamageNeed[] = [];
    const scope = resolveObjectDestruction({ encounterId, campaignId: encounterId, inventoryId: inventoryId(target), isGM: true,
      splashChild: true, branches: { door: true, spill: true, corpse: true, wall: true, payload: true }, breakingDamage: hit.total,
      payloadCaster: { kind: 'creature', id: ecId(caster), position: {
        x_pos: Number(posCoord(caster, 'x_pos') ?? 0), y_pos: Number(posCoord(caster, 'y_pos') ?? 0) } },
      destroyedByDamageTypes: hit.damageTypeIds }, view, { ...destructionReaders, hiddenLinks: id => {
        // Fetch inherited links through the original persisted anchor.
        for (let hops = 0; createdIds.has(upper(id)); hops++) {
          if (hops >= 32) handBack('inventory-damage:link-ancestry-cycle');
          const origin = linkOrigins.get(upper(id));
          if (!origin) return false;
          id = origin;
        }
        const held = destructionReaders.hiddenLinks ? destructionReaders.hiddenLinks(id) : false;
        if (held === null) {
          needs.push({ kind: 'links', id: upper(id) });
          // Discovery only: reach this row's corpse/spill readers, then discard the entire provisional plan below.
          if (ctx.dryRun) return false;
        }
        return held;
      }, corpseBody: id => {
        const body = destructionReaders.corpseBody?.(id) ?? null;
        if (ctx.dryRun && (body === null || !ctx.members.has(upper(id)))) {
          needs.push({ kind: 'corpse', id: upper(id) });
          return body ?? [];
        }
        return body;
      } });
    if (needs.length) throw new InventoryDamageFactsNeeded(needs);
    if (!scope.supported) {
      if (!scope.refused && scope.corpseCreatureIds?.length)
        handBack(`inventory-damage:corpse-not-held:${scope.corpseCreatureIds[0]}`);
      const corpse = !scope.refused ? scope.definitionIds?.find(id => id.startsWith('corpse:')) : undefined;
      if (corpse) handBack(`inventory-damage:corpse-not-held:${corpse.slice('corpse:'.length)}`);
      if (!scope.refused && (scope.anchorItemId || scope.definitionIds?.length))
        definitionNotHeld('inventory-damage:definition-not-held', scope.anchorItemId ?? scope.definitionIds![0]);
      const reason = scope.refused
        ? `refused:${scope.message.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'unspecified'}` : scope.reason;
      handBack(`inventory-damage:destruction:${reason}`);
    }
    destruction = scope.command;
    if (destruction.wall?.kind === 'section') for (const added of destruction.frame.add) {
      const generation = record(added.inventory.inventory_generation);
      if (!generation) continue;
      const template = ctx.read.definition(added.inventory.inventory_item_id)!;
      const vitality = record(createVitalityReader({ resolveInventoryInstance: () => null })(template));
      const columns = added.inventory as unknown as Row;
      columns.v_max = Number(vitality?.v_max ?? 5);
      columns.v_current = Number(vitality?.v_current ?? columns.v_max);
      columns.v_temp = Number(vitality?.v_temp ?? 0);
      generation.fingerprint = wallPieceFingerprint({ ...columns, ...added.position });
    }
    planCorpseCleanup(ctx, destruction);
    prepareDestructionSave(destruction, id => ctx.read.definition(id));
    destruction.caster_id = ecId(caster);
    const log = (command: ObjectDestroyCommand, source: Row): void => {
      for (const nested of command.spill?.destroyed ?? []) log(nested, { inventory: { inventory_id: nested.inventory_id } });
      if (command.spill?.log) insertRoll(ctx, { roll_type_1: 'inventory_container_spilled', roll_item_id: command.inventory_id,
        by: caster, to: source });
      if (command.corpse) insertRoll(ctx, { roll_type_1: 'corpse_destroyed', roll_item_id: command.inventory_id, by: caster });
      insertRoll(ctx, { roll_type_1: command.log.key, roll_item_id: command.inventory_id, by: caster, to: source });
    };
    log(destruction, target);
    cancelDestroyedInventoryStatuses(ctx, destruction.frame.remove);
    applyObjectDestruction(ctx.encounter, destruction.frame);
  }
  const maximum = Number(record(record(target.computed)?.vitality)?.v_max ?? 0);
  const inherited = Number(readInventoryField(target, 'v_max') ?? 0) <= 0 && maximum > 0;
  ctx.inventoryChanged.push({ inventory_id: hit.inventory_id, changes: { v_current: hit.v_current, v_temp: hit.v_temp,
    ...(!hit.destroyed && inherited ? { v_max: maximum } : {}) },
    ...(destruction ? { destruction } : {}) });
  if (!hit.destroyed && inherited) record(target.inventory)!.v_max = maximum;
}

/** Existing inventory ids a save addresses, including contents which were never floor roster members. */
export function abilityInventoryTargetIds(changes: JanusCtx['inventoryChanged']): string[] {
  return [...new Set(changes.flatMap(change => [change.inventory_id, ...(change.destruction?.frame.remove ?? []),
    ...(change.destruction?.frame.add ?? []).map(row => inventoryId(row as unknown as Row)),
    ...(change.destruction?.frame.released ?? []).map(row => inventoryId(row)), ...(change.carve?.rows ?? []).map(row => inventoryId(row))]))];
}
